import {
  generatePostbackLink,
  getContract,
  type NormalizedEvent,
} from '@postback-receiver/postback-contracts';

/**
 * Data module for LinksPage.tsx, split out of the component file so it can
 * export plain constants/functions without tripping the
 * react-refresh/only-export-components rule (component files must only
 * export components).
 */

export interface EventTemplate {
  name: string;
  event: string;
  /** Legacy format: `brand={brand}&event=...` — what every partner panel already holds. */
  params: string;
  /** New format: the same query with `brand_id={brand_id}` in front of `brand=`. */
  paramsWithBrandId: string;
  description: string;
}

/**
 * Display metadata (title, prose description) per normalized event. Not part of
 * the contract — presentation-only, identical across every platform today.
 */
const EVENT_DISPLAY: Record<NormalizedEvent, { name: string; description: string }> = {
  REG: { name: 'Registration', description: 'User signup event' },
  FTD: { name: 'FTD', description: 'First time deposit event' },
  QUALIFIED_CPA: { name: 'CPA', description: 'CPA qualification event (primary revenue)' },
  REDEP: { name: 'REDEP', description: 'Repeat deposit event' },
  DEP: { name: 'DEP', description: 'Deposit event' },
  CLICK: { name: 'CLICK', description: 'Click event' },
};

/**
 * `brand={brand}&event=<value>&<param>=<macro>&...` — the exact string this
 * page has always interpolated after `${baseUrl}${path}?`. Delegates to the
 * package's own generator (not a local reimplementation) so this can never
 * drift from what the contract actually produces.
 */
function eventQuery(contractId: string, event: NormalizedEvent, brandId?: string): string {
  const contract = getContract(contractId);
  if (!contract) throw new Error(`Unknown postback contract id "${contractId}"`);
  const full = generatePostbackLink(contract, event, { baseUrl: '', brand: '{brand}', brandId });
  return full.slice(full.indexOf('?') + 1);
}

/** Builds a PLATFORM_TEMPLATES `events` array from a packages/postback-contracts contract id. */
function eventsFromContract(contractId: string): EventTemplate[] {
  const contract = getContract(contractId);
  if (!contract) throw new Error(`Unknown postback contract id "${contractId}"`);
  return contract.events.map((tpl) => {
    const display = EVENT_DISPLAY[tpl.event];
    return {
      name: display.name,
      event: tpl.eventValue,
      params: eventQuery(contractId, tpl.event),
      paramsWithBrandId: eventQuery(contractId, tpl.event, '{brand_id}'),
      description: display.description,
    };
  });
}

/**
 * Platform-specific event templates with native macros.
 * Matches the bot's /links command output.
 */
export const PLATFORM_TEMPLATES: Record<string, { events: EventTemplate[]; notes: string[] }> = {
  '/postbacks/custom': {
    events: eventsFromContract('custom'),
    notes: [
      'Macros: {clickid}, {subid}, {subid2}...{subid5}, {payout}',
      'tracking_tag: {clickid} — campaign analytics tag (e.g., WA_FR_DIM_20)',
    ],
  },
  // 'referon-builder' reproduces this page's existing (unconfirmed) builder-consensus template,
  // not the DTO/Highspin-confirmed 'referon' contract — see docs/solutions/template-surfaces-migration.md.
  '/postbacks/referon': {
    events: eventsFromContract('referon-builder'),
    notes: [
      'Macros: ${mid}, ${customerid}, ${dynamic_variable}, ${eventType}, ${eventTime}, ${brandGuid}, ${clickId}',
      'tracking_tag: ${clickId} — campaign analytics tag',
    ],
  },
  '/postbacks/affise': {
    events: eventsFromContract('affise'),
    notes: [
      'Macros: {clickid}, {pid} (partner ID), {payout}, {sum}, {goal}, {visit_id}',
      'tracking_tag: {visit_id} — campaign analytics tag',
    ],
  },
  '/postbacks/sales-expert': {
    events: eventsFromContract('cellxpert'),
    notes: [
      'Macros: [trackingcode], [afp], [isocountry], [commissionamount]',
      'tracking_tag: [afp] — campaign analytics tag',
    ],
  },
  '/postbacks/traffickong': {
    events: eventsFromContract('traffickong'),
    notes: [
      'Macros: {clickid} (mapped from ui=clickid), {subid}, {payout}',
      'tracking_tag: {clickid} — campaign analytics tag',
    ],
  },
  '/postbacks/cparound': {
    events: eventsFromContract('cparound'),
    notes: [
      'Macros: {clickid}, {subid}, {payout}',
      'tracking_tag: {clickid} — campaign analytics tag',
    ],
  },
  '/postbacks/keitaro': {
    events: eventsFromContract('keitaro'),
    notes: [
      'Macros: {subid}, {campaign_id}, {payout}',
      'tracking_tag: {subid} — campaign analytics tag',
    ],
  },
  '/postbacks/everflow': {
    events: eventsFromContract('everflow'),
    notes: [
      'Macros: {transaction_id}, {offer_id}, {payout}, {amount}',
      'tracking_tag: {transaction_id} — campaign analytics tag',
    ],
  },
  // 'affilka-native' reproduces this page's existing dedicated-endpoint template (route
  // /postbacks/affilka), not the generic-route 'affilka' contract used by backoffice/Telegram —
  // see docs/solutions/template-surfaces-migration.md.
  '/postbacks/affilka': {
    events: eventsFromContract('affilka-native'),
    notes: [
      'Macros: ${player_id}, ${campaign_id}, ${promo_id}, ${visit_id}, ${amount}, ${currency}',
      'tracking_tag: ${visit_id} — campaign analytics tag',
    ],
  },
  '/postbacks/cellxpert': {
    events: eventsFromContract('cellxpert-native'),
    notes: [
      'Macros: [trackingcode], [afp], [isocountry], [commissionamount]',
      'tracking_tag: [afp] — campaign analytics tag',
    ],
  },
};
